import { useCallback } from 'react'

import { useTheme } from 'next-themes'

import { useAuthInfo } from '@sweetauth/core'

import { getPersonColor } from '@/components/library-v2/people/lib'

import { useAgencies } from '@/hooks/library/useAgencies'
import { useCompanies } from '@/hooks/library/useCompanies'
import { useCompanyDepartments } from '@/hooks/library/useDepartments'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  EntityAgency,
  EntityCompany,
  EntityCompanyDepartment,
  EntityPerson,
} from '@/types/library/Entity'

interface UseThemeAdjustedPersonAvatarColorProps {
  person: EntityPerson
}

export const useThemeAdjustedPersonAvatarColor = ({
  person,
}: UseThemeAdjustedPersonAvatarColorProps) => {
  const { theme } = useTheme()
  return getPersonColor(person, theme ?? 'light')
}

interface UsePersonDepartmentProps {
  person: EntityPerson
}

export const usePersonDepartment = ({ person }: UsePersonDepartmentProps) => {
  const { activeOrgId } = useActiveOrgId()
  const selectDepartment = useCallback(
    (response: PaginatedResponse<EntityCompanyDepartment>) =>
      response.data.find((dept) => dept.id === person.entity_department_id) ??
      null,
    [person.entity_department_id],
  )

  return useCompanyDepartments({
    orgId: activeOrgId,
    companyId: person.entity_company_id ?? '',
    select: selectDepartment,
  })
}

interface UsePersonCompanyProps {
  person: EntityPerson
}

export const usePersonCompany = ({ person }: UsePersonCompanyProps) => {
  const { activeOrgId } = useActiveOrgId()
  const selectCompany = useCallback(
    (response: PaginatedResponse<EntityCompany>) =>
      response.data.find(
        (company) => company.id === person.entity_company_id,
      ) ?? null,
    [person.entity_company_id],
  )

  return useCompanies({
    orgId: activeOrgId,
    select: selectCompany,
    enabled: !!person.entity_company_id,
  })
}

interface UsePersonAgencyProps {
  person: EntityPerson
}

export const usePersonAgency = ({ person }: UsePersonAgencyProps) => {
  const { activeOrgId } = useActiveOrgId()
  const agencyId = person.entity_agency_id ?? null
  const selectAgency = useCallback(
    (response: PaginatedResponse<EntityAgency>) =>
      response.data.find((agency) => agency.id === agencyId) ?? null,
    [agencyId],
  )

  return useAgencies({
    orgId: activeOrgId,
    select: selectAgency,
    enabled: !!agencyId,
  })
}

interface UseIsYouProps {
  userId: UUID | null
}

/**
 * Check if the given user_id matches the currently authenticated user.
 * Useful for showing "You" badges or restricting edit access.
 */
export const useIsYou = ({ userId }: UseIsYouProps): boolean => {
  const { user } = useAuthInfo()
  return !!userId && !!user && userId === user.userId
}
